// src/pages/TextTools/ImageGenerator.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { generateImage, checkImageStatus } from '../../services/chatApi';
import { Download, Image as ImageIcon, Loader2, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €";

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};

const ImageGenerator = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    const [prompt, setPrompt] = useState('');
    const [imageFormat, setImageFormat] = useState('PNG');
    const [generatedImage, setGeneratedImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [taskId, setTaskId] = useState(null);
    const [taskStatus, setTaskStatus] = useState(null);
    const [retryInfo, setRetryInfo] = useState(null);

    const imageFormats = ['PNG', 'JPEG', 'WebP', 'GIF', 'TIFF', 'BMP'];

    // Effect to poll task status when taskId is set
    useEffect(() => {
        let interval = null;
        if (taskId && taskStatus === 'processing') {
            interval = setInterval(async () => {
                try {
                    const statusResponse = await checkImageStatus(taskId);
                    setTaskStatus(statusResponse.status);

                    if (statusResponse.status === 'completed') {
                        setGeneratedImage({
                            data: `data:image/${statusResponse.image_format.toLowerCase()};base64,${statusResponse.image_data}`,
                            format: statusResponse.image_format
                        });
                        clearInterval(interval);
                        
                        // Update credits if returned
                        if (statusResponse.new_credits !== undefined) {
                            console.log(`Credits updated. New balance: ${statusResponse.new_credits}`);
                            setCredits(statusResponse.new_credits);
                        }
                        
                        setTaskId(null);
                        setIsLoading(false);
                        setTaskStatus(null);
                        setRetryInfo(null);
                    } else if (statusResponse.status === 'failed') {
                        setError(statusResponse.error || t('imageGenerator.errorGeneric'));
                        clearInterval(interval);
                        setTaskId(null);
                        setIsLoading(false);
                        setTaskStatus(null);
                        setRetryInfo(null);
                    }
                } catch (err) {
                    console.error('Error checking task status:', err);
                    clearInterval(interval);
                    setTaskId(null);
                    setError(t('imageGenerator.statusCheckError'));
                    setIsLoading(false);
                    setTaskStatus(null);
                    setRetryInfo(null);
                }
            }, 2000); // Check every 2 seconds
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [taskId, taskStatus, t, setCredits]);

    const handleGenerate = async () => {
        setError(null);
        setGeneratedImage(null);
        setTaskStatus(null);
        setTaskId(null);
        setRetryInfo(null);

        if (!prompt.trim()) {
            setError(t('imageGenerator.enterPromptFirst'));
            return;
        }

        setIsLoading(true);

        try {
            const result = await generateImage(prompt, imageFormat);
            
            if (result.task_id) {
                setTaskId(result.task_id);
                setTaskStatus('processing');
            } else {
                setError(t('imageGenerator.errorGeneric'));
                setIsLoading(false);
                setTaskStatus(null);
            }
        } catch (err) {
            setError(err.message || t('imageGenerator.errorGeneric'));
            setIsLoading(false);
            setTaskStatus(null);
        }
    };

    const handleDownload = () => {
        if (!generatedImage) return;

        const link = document.createElement('a');
        link.href = generatedImage.data;
        
        const formatExtensions = {
            'PNG': 'png',
            'JPEG': 'jpg',
            'WEBP': 'webp',
            'GIF': 'gif',
            'TIFF': 'tiff',
            'BMP': 'bmp'
        };
        
        const extension = formatExtensions[generatedImage.format] || 'png';
        link.download = `generated_image.${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleClear = () => {
        setPrompt('');
        setGeneratedImage(null);
        setError(null);
        setTaskId(null);
        setTaskStatus(null);
        setRetryInfo(null);
        setImageFormat('PNG');
    };

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                <SubscribePrompt />
            ) : user?.credits <= 0 ? (
                <PurchaseCreditsPrompt />
            ) : (
                <div className="max-w-4xl mx-auto">
                    <h3 className="text-3xl font-bold text-brand-orange">
                        {t('imageGenerator.title')}
                    </h3>
                    <p className="mt-2 text-brand-dark">
                        {t('imageGenerator.description')}
                    </p>

                    {/* Info Box */}
                    <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-sm text-blue-800">
                            💡 {t('imageGenerator.tip')}
                        </p>
                    </div>

                    {/* Prompt Input Section */}
                    <div className="mt-6">
                        <label className="block text-sm font-medium mb-2">
                            {t('imageGenerator.promptLabel')}
                        </label>
                        <textarea
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder={t('imageGenerator.promptPlaceholder')}
                            className="w-full p-3 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none min-h-[120px] resize-y rounded-md"
                            disabled={isLoading}
                        />
                        <p className="text-xs text-slate-600 mt-1">
                            {t('imageGenerator.promptHint')}
                        </p>
                    </div>

                    {/* Format Selection */}
                    <div className="mt-4">
                        <label className="block text-sm font-medium mb-2">
                            {t('imageGenerator.formatLabel')}
                        </label>
                        <select
                            value={imageFormat}
                            onChange={(e) => setImageFormat(e.target.value)}
                            className="w-full md:w-64 p-2 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none rounded-md"
                            disabled={isLoading}
                        >
                            {imageFormats.map(format => (
                                <option key={format} value={format}>{format}</option>
                            ))}
                        </select>
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
                            {error}
                        </div>
                    )}

                    {/* Retry Info Display */}
                    {retryInfo && (
                        <div className="mt-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded-md">
                            {retryInfo}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-4 mt-6">
                        <button
                            onClick={handleGenerate}
                            className="px-6 py-2 border border-brand-orange hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors disabled:bg-slate-500 disabled:cursor-not-allowed flex items-center"
                            disabled={isLoading || taskStatus === 'processing' || !prompt.trim()}
                        >
                            {isLoading || taskStatus === 'processing' ? (
                                <>
                                    <Loader2 className="animate-spin mr-2" size={20} />
                                    {t('imageGenerator.generatingButton')}
                                </>
                            ) : (
                                <>
                                    <Sparkles className="mr-2" size={20} />
                                    {t('imageGenerator.generateButton')}
                                </>
                            )}
                        </button>
                        <button
                            onClick={handleClear}
                            className="px-6 py-2 border border-gray-600 hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors"
                            disabled={isLoading || taskStatus === 'processing'}
                        >
                            {t('imageGenerator.clearButton')}
                        </button>
                    </div>

                    {/* Loading Status */}
                    {(isLoading || taskStatus === 'processing') && (
                        <div>
                            
                        </div>
                    )}

                    {/* Display Generated Image */}
                    {generatedImage && (
                        <div className="mt-10">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-2xl font-semibold">
                                    {t('imageGenerator.generatedImage')}
                                </h4>
                            </div>
                            
                            <div className="relative border border-slate-300 rounded-md overflow-hidden bg-white shadow-lg">
                                <img 
                                    src={generatedImage.data} 
                                    alt="Generated" 
                                    className="w-full h-auto"
                                />
                            </div>

                            {/* Image Info */}
                            <div className="mt-2 text-sm text-slate-600">
                                {t('imageGenerator.imageFormat')}: {generatedImage.format} | 
                                {t('imageGenerator.imageDimensions')}: 1024 x 768
                            </div>

                            {/* Download Button */}
                            <div className="flex gap-4 mt-6">
                                <button
                                    onClick={handleDownload}
                                    className="px-4 py-2 bg-brand-orange text-white rounded-md hover:bg-orange-600 transition-colors flex items-center"
                                >
                                    <Download size={14} className="mr-2" />
                                    {t('imageGenerator.downloadButton', { format: generatedImage.format })}
                                </button>
                            </div>

                            {/* Prompt Used */}
                            <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-md">
                                <p className="text-sm font-medium text-slate-700 mb-2">
                                    {t('imageGenerator.promptUsed')}:
                                </p>
                                <p className="text-sm text-slate-600 italic">
                                    "{prompt}"
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ImageGenerator;
